import React from 'react'
import { useTranslation } from 'react-i18next'
import { Bell } from 'lucide-react'
import { useTaskReminderRows } from '../../../hooks/useTaskReminderRows'
import { useReminderActions } from '../../../hooks/useReminderActions'
import { reminderWhenLabel } from '../../../utils/reminderLabels'
import { ReminderListContent } from '../../ReminderList'
import { DetailsChip } from './DetailsChip'

export interface DetailsReminderChipProps {
  taskId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** `'at'`: gleich beim Anlegen, auf „Datum & Uhrzeit“ — so verspricht es der Menüeintrag. */
  tab?: 'at'
}

/**
 * **Der Chip „Erinnerung“** im Kopf der Aufgaben-Details (Plan task-reminders, Entscheidung 12).
 *
 * Er zeigt den WERT wie die anderen Chips (Glossar): den nächsten Zeitpunkt, „+2“ dahinter bei
 * mehreren; sind alle still, die erste gedämpft; ohne Erinnerung die Beschriftung. Das Popover
 * listet alle und legt neue an ({@link ReminderListContent}).
 *
 * Ein eigener Baustein, damit nur eine EINZELNE Aufgabe die Abfragen der Erinnerungen zahlt — die
 * übrigen Chips gelten auch einer Auswahl, dieser nicht.
 */
export const DetailsReminderChip: React.FC<DetailsReminderChipProps> = ({ taskId, open, onOpenChange, tab }) => {
  const { t, i18n } = useTranslation()
  const rows = useTaskReminderRows(taskId)
  const actions = useReminderActions(taskId)

  const count = rows?.rows.length ?? 0
  const locale = i18n.language || 'de'
  let value: React.ReactNode = t('taskDetails.section.reminders')
  if (rows && count > 0) {
    const more = count > 1 ? <span className="details-chip__more">{t('reminders.chip.more', { count: count - 1 })}</span> : null
    value = rows.next
      ? <>{reminderWhenLabel(rows.next, new Date(), t, locale)}{more}</>
      : <><span className="details-chip__muted">{rows.rows[0]!.label}</span>{more}</>
  }

  return (
    <DetailsChip
      id="reminders"
      icon={<Bell size={16} />}
      filled={count > 0}
      open={open}
      onOpenChange={onOpenChange}
      content={rows && (
        <ReminderListContent
          rows={rows.rows}
          anchors={rows.anchors}
          anchorSilence={rows.anchorSilence}
          onAdd={actions.add}
          onRemove={actions.remove}
          permission={actions.permission}
          onRequestPermission={actions.requestPermission}
          defaultAdding={tab === 'at'}
          defaultTab={tab}
        />
      )}
    >
      {value}
    </DetailsChip>
  )
}
